import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import ReactDOM from 'react-dom';
import {
  BookOpen, PenLine, Search, Star, StarOff, Trash2, X,
  HeartHandshake, Sparkles, BookMarked, Sprout, Heart, Lock,
  Filter, Plus, Check, Calendar, Tag, ChevronRight, ChevronLeft,
  MessageCircle, NotebookPen, Flame, Sunrise,
} from 'lucide-react';
import { journalApi } from '../api/client';
import { sanitizeForDisplay } from '../utils/richText';
import { useTranslation } from '../i18n';
import enLocale from '../locales/en';
import esLocale from '../locales/es';
import PageHeader from '../components/PageHeader';
import PageGuide from '../components/PageGuide';
import './JournalScreen.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const TYPE_META = {
  prayer_event:      { icon: <HeartHandshake size={13}/>, color: '#0d9488', bg: 'rgba(13,148,136,0.08)' },
  memorial_prayer:   { icon: <span style={{fontSize:13}}>🕯️</span>, color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)' },
  pray_now:          { icon: <Sparkles size={13}/>,       color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
  bible_study:       { icon: <BookOpen size={13}/>,       color: '#2563eb', bg: 'rgba(37,99,235,0.08)'  },
  brp_reflection:    { icon: <BookMarked size={13}/>,     color: '#0d9488', bg: 'rgba(13,148,136,0.08)' },
  growth_reflection: { icon: <Sprout size={13}/>,         color: '#16a34a', bg: 'rgba(22,163,74,0.08)'  },
  faith_checkin:     { icon: <Heart size={13}/>,          color: '#ec4899', bg: 'rgba(236,72,153,0.08)' },
  evangelism_checkin:{ icon: <Flame size={13}/>,          color: '#E8751A', bg: 'rgba(232,117,26,0.08)' },
  journal_entry:     { icon: <PenLine size={13}/>,        color: '#6366f1', bg: 'rgba(99,102,241,0.08)' },
  well_discussion:   { icon: <MessageCircle size={13}/>,  color: '#0891b2', bg: 'rgba(8,145,178,0.08)'  },
  well_note:         { icon: <NotebookPen size={13}/>,    color: '#7c3aed', bg: 'rgba(124,58,237,0.08)' },
  daily_rhythm_reflection: { icon: <Sunrise size={13}/>, color: '#E8751A', bg: 'rgba(232,117,26,0.08)' },
};

const CHECKIN_COLORS = ['#dc2626','#ea580c','#d97706','#ca8a04','#16a34a','#0d9488','#2563eb'];

// "#0d9488" → "13, 148, 136" so CSS can do rgba(var(--hl), <alpha>) for the highlighter
function hexToRgbTriplet(hex) {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return '120, 120, 120';
  return `${parseInt(h.slice(0,2),16)}, ${parseInt(h.slice(2,4),16)}, ${parseInt(h.slice(4,6),16)}`;
}

const TYPE_FILTERS = [
  { key: 'all',              labelKey: 'filterAll'         },
  { key: 'prayer_event',     labelKey: 'filterPrayer'      },
  { key: 'pray_now',         labelKey: 'filterPrayNow'     },
  { key: 'bible_study',      labelKey: 'filterBibleStudy'  },
  { key: 'brp_reflection',   labelKey: 'filterReflections' },
  { key: 'growth_reflection',labelKey: 'filterReflections' },
  { key: 'faith_checkin',    labelKey: 'filterCheckin'     },
  { key: 'evangelism_checkin', labelKey: 'filterEvangelism' },
  { key: 'daily_rhythm_reflection', labelKey: 'filterRhythm' },
  { key: 'journal_entry',    labelKey: 'filterEntries'     },
];

const UNIQUE_FILTERS = [
  { key: 'all',              labelKey: 'filterAll'         },
  { key: 'prayer',           labelKey: 'filterPrayer',     types: ['prayer_event','memorial_prayer'] },
  { key: 'pray_now',         labelKey: 'filterPrayNow',    types: ['pray_now'] },
  { key: 'bible_study',      labelKey: 'filterBibleStudy', types: ['bible_study'] },
  { key: 'reflections',      labelKey: 'filterReflections',types: ['brp_reflection','growth_reflection'] },
  { key: 'well',             labelKey: 'filterWell',       types: ['well_discussion','well_note'] },
  { key: 'faith_checkin',    labelKey: 'filterCheckin',    types: ['faith_checkin'] },
  { key: 'evangelism_checkin', labelKey: 'filterEvangelism', types: ['evangelism_checkin'] },
  { key: 'daily_rhythm',     labelKey: 'filterRhythm',     types: ['daily_rhythm_reflection'] },
  { key: 'journal_entry',    labelKey: 'filterEntries',    types: ['journal_entry'] },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function fmtDate(d, lang) {
  const date = new Date(d);
  return date.toLocaleDateString(lang || undefined, { weekday:'long', month:'long', day:'numeric', year:'numeric' });
}

function fmtTime(d, lang) {
  return new Date(d).toLocaleTimeString(lang || undefined, { hour:'numeric', minute:'2-digit' });
}

// Sunday 00:00 local time of the week containing `d`.
function startOfWeekSunday(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay());
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}

function isThisWeek(weekStart) {
  return isSameDay(weekStart, startOfWeekSunday(new Date()));
}

// ISO 8601 week number. The journal uses Sunday-based weeks, so callers must
// pass a date that lands inside the intended ISO week (we pass weekStart + 3).
function isoWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// Return YYYY-MM-DD using local-time components (matches server's localDateKey).
function fmtLocalDateKey(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fmtWeekRange(weekStart, lang) {
  const end = addDays(weekStart, 6);
  const loc = lang || undefined;
  const sameMonth = weekStart.getMonth() === end.getMonth()
    && weekStart.getFullYear() === end.getFullYear();
  const sameYear  = weekStart.getFullYear() === end.getFullYear();
  const year      = end.getFullYear();

  if (sameMonth) {
    const month = weekStart.toLocaleDateString(loc, { month: 'short' });
    return `${month} ${weekStart.getDate()}–${end.getDate()}, ${year}`;
  }
  if (sameYear) {
    const s = weekStart.toLocaleDateString(loc, { month: 'short', day: 'numeric' });
    const e = end.toLocaleDateString(loc,       { month: 'short', day: 'numeric' });
    return `${s} – ${e}, ${year}`;
  }
  const s = weekStart.toLocaleDateString(loc, { month: 'short', day: 'numeric', year: 'numeric' });
  const e = end.toLocaleDateString(loc,       { month: 'short', day: 'numeric', year: 'numeric' });
  return `${s} – ${e}`;
}

function translateRelationship(rel, lang) {
  if (!rel) return rel;
  const enList = enLocale.prayer?.relationships || [];
  const idx = enList.findIndex(r => r.toLowerCase() === rel.toLowerCase());
  if (idx === -1) return rel;
  const localeList = (lang === 'es' ? esLocale : enLocale).prayer?.relationships || [];
  return localeList[idx] || rel;
}

function fmtDateKey(d) {
  const date = new Date(d);
  return date.toLocaleDateString('en-CA'); // YYYY-MM-DD
}

function groupByDate(items) {
  const groups = {};
  for (const item of items) {
    const key = fmtDateKey(item.date);
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }
  // Day groups: ascending (Sun first), matching the week strip left→right.
  // Items within each day: earliest first (chronological).
  return Object.entries(groups)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, items]) => [key, [...items].reverse()]);
}

function wordCount(text) {
  return (text || '').trim().split(/\s+/).filter(Boolean).length;
}

// ── Ad-hoc Entry Modal ────────────────────────────────────────────────────────

function EntryModal({ entry, onClose, onSaved }) {
  const { t } = useTranslation('journal');
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    title:     entry?.title     || '',
    body:      entry?.body      || '',
    tags:      (entry?.tags     || []).join(', '),
    entryDate: entry?.entryDate ? new Date(entry.entryDate).toISOString().slice(0,10) : today,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const bodyRef = useRef(null);

  useEffect(() => { bodyRef.current?.focus(); }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.body.trim()) { setError('Please write something first.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        title:     form.title.trim() || null,
        body:      form.body.trim(),
        tags:      form.tags.split(',').map(t => t.trim()).filter(Boolean),
        entryDate: form.entryDate,
      };
      const saved = entry
        ? await journalApi.updateEntry(entry.id, payload)
        : await journalApi.createEntry(payload);
      onSaved(saved.entry || saved, !!entry);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not save.');
    } finally { setSaving(false); }
  }

  return (
    <div className="jn-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="jn-modal fade-up">
        <div className="jn-modal-header">
          <span className="jn-modal-title">
            <PenLine size={15} style={{marginRight:6}}/>
            {entry ? t('editEntry') : t('newEntry')}
          </span>
          <button className="jn-modal-close" onClick={onClose}><X size={16}/></button>
        </div>

        {error && <div className="jn-modal-error">{error}</div>}

        <div className="jn-modal-body">
          <div className="jn-field">
            <label className="jn-label">{t('entryTitle')}</label>
            <input className="jn-input" value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder={t('entryTitlePlaceholder')}/>
          </div>

          <div className="jn-field">
            <label className="jn-label">{t('entryBody')} <span style={{color:'var(--text-lt)',fontWeight:400,fontSize:11}}>({wordCount(form.body)} words)</span></label>
            <textarea ref={bodyRef} className="jn-input jn-textarea" rows={8}
              value={form.body}
              onChange={e => set('body', e.target.value)}
              placeholder={t('entryBodyPlaceholder')}/>
          </div>

          <div className="jn-field-row">
            <div className="jn-field" style={{flex:1}}>
              <label className="jn-label"><Calendar size={12}/> {t('entryDate')}</label>
              <input type="date" className="jn-input" value={form.entryDate}
                max={today}
                onChange={e => set('entryDate', e.target.value)}/>
            </div>
            <div className="jn-field" style={{flex:2}}>
              <label className="jn-label"><Tag size={12}/> {t('entryTags')}</label>
              <input className="jn-input" value={form.tags}
                onChange={e => set('tags', e.target.value)}
                placeholder={t('entryTagsPlaceholder')}/>
            </div>
          </div>
        </div>

        <div className="jn-modal-footer">
          <button className="btn btn-outline" onClick={onClose}>{t('common.cancel', null, {ns:'common'}) || 'Cancel'}</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.body.trim()}>
            {saving ? <span className="spinner" style={{width:14,height:14}}/> : <Check size={14}/>}
            {t('saveEntry')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Entry Card Components ─────────────────────────────────────────────────────

function PrayerEventCard({ item, t, lang }) {
  const meta = TYPE_META[item.type];
  const isMemorial = item.type === 'memorial_prayer';

  return (
    <div className="jn-entry-inner">
      <div className="jn-entry-who">
        <span className="jn-entry-name">
          {isMemorial ? t('remembering') : t('prayedFor')} <strong>{item.recipient?.name}</strong>
        </span>
        {item.recipient?.relationship && (
          <span className="jn-entry-rel">{translateRelationship(item.recipient.relationship, lang)}</span>
        )}
        {item.recipient?.faithLegacy && (
          <div className="jn-legacy-note">
            <span style={{color: meta.color, marginRight:5}}>✦</span>{item.recipient.faithLegacy}
          </div>
        )}
      </div>
      {/* A calendar event's notes are the same rich text as a recipient's prayer
          request — the server mirrors one onto the other — so they render as HTML.
          `jn-entry-rich` drops the pre-wrap that would double every <br> and
          zeroes block margins, because the 36px line grid is unforgiving. */}
      {sanitizeForDisplay(item.notes) && (
        <div className="jn-entry-section">
          <div className="jn-entry-section-label">{t('myNotes')}</div>
          <div className="jn-entry-text jn-entry-rich"
            dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(item.notes) }} />
        </div>
      )}
      {item.prayer && (
        <div className="jn-entry-section">
          <div className="jn-entry-section-label">{t('prayerGenerated')}</div>
          <div className="jn-entry-prayer" style={{borderLeft:`3px solid ${meta.color}`}}>
            {item.prayer}
          </div>
        </div>
      )}
    </div>
  );
}

function PrayNowCard({ item, t }) {
  const meta = TYPE_META.pray_now;
  const chips = [
    item.moment  && { label: t('moment'), val: item.moment },
    item.tone    && { label: t('tone'),   val: item.tone   },
    item.forWho  && { label: t('forWho'), val: item.forWho },
    item.withWho && { label: t('withWho'),val: item.withWho },
    item.need    && { label: t('need'),   val: item.need   },
  ].filter(Boolean);

  return (
    <div className="jn-entry-inner">
      {chips.length > 0 && (
        <div className="jn-entry-chips">
          {chips.map(c => (
            <span key={c.label} className="jn-chip" style={{background: meta.bg, color: meta.color}}>
              <span style={{opacity:0.7,fontSize:10,marginRight:3}}>{c.label}:</span>{c.val}
            </span>
          ))}
        </div>
      )}
      <div className="jn-entry-prayer" style={{borderLeft:`3px solid ${meta.color}`}}>
        {item.prayer}
      </div>
    </div>
  );
}

function BibleStudyCard({ item, t }) {
  const meta = TYPE_META.bible_study;
  const qa = [
    { q: t('aboutGod'),     a: item.answers?.aboutGod     },
    { q: t('aboutHumans'),  a: item.answers?.aboutHumans  },
    { q: t('howRespond'),   a: item.answers?.howRespond   },
    { q: t('sharePrompt'),  a: item.answers?.sharePrompt  },
  ].filter(x => x.a);

  return (
    <div className="jn-entry-inner">
      <div className="jn-entry-passage" style={{color: meta.color}}>
        <BookOpen size={13}/> {item.reference}
      </div>
      {qa.map((x, i) => (
        <div key={i} className="jn-entry-qa">
          <div className="jn-entry-q">{x.q}</div>
          <div className="jn-entry-a">{x.a}</div>
        </div>
      ))}
    </div>
  );
}

function ReflectionCard({ item, t }) {
  const isBRP = item.type === 'brp_reflection';
  const meta  = TYPE_META[item.type];
  const unitLabel = item.cadence === 'week' ? t('week') : t('day');
  const unitNum   = isBRP ? item.dayNumber : item.unitNumber;
  const unitTitle = item.dayTitle || item.unitTitle;

  const answers = item.answers || {};
  const questions = item.questions || [];

  return (
    <div className="jn-entry-inner">
      <div className="jn-entry-plan-meta">
        {item.planTitle && (
          <span className="jn-chip" style={{background: item.planColor ? item.planColor + '22' : meta.bg, color: item.planColor || meta.color}}>
            {item.planTitle}
          </span>
        )}
        {unitNum && (
          <span className="jn-chip" style={{background: meta.bg, color: meta.color}}>
            {unitLabel} {unitNum}{unitTitle ? ` · ${unitTitle}` : ''}
          </span>
        )}
        {isBRP && item.community && (
          <span className="jn-chip" style={{background:'var(--gray-1)',color:'var(--text-lt)'}}>
            {item.community.name}
          </span>
        )}
      </div>
      {item.verses && (
        <div className="jn-entry-passage" style={{color: meta.color}}>
          <BookOpen size={13}/> {item.verses}
        </div>
      )}
      {questions.map((q, i) => {
        const a = answers[`q${i}`] || answers[i] || answers[String(i)];
        if (!a) return null;
        return (
          <div key={i} className="jn-entry-qa">
            <div className="jn-entry-q">{q}</div>
            <div className="jn-entry-a">{a}</div>
          </div>
        );
      })}
    </div>
  );
}

function FaithCheckinCard({ item, t }) {
  const meta   = TYPE_META.faith_checkin;
  const labels = t('checkinLevels');
  const label  = Array.isArray(labels) ? labels[(item.score || 1) - 1] : '';
  const color  = CHECKIN_COLORS[(item.score || 1) - 1];
  const pct    = ((item.score || 1) - 1) / 6 * 100;

  return (
    <div className="jn-entry-inner">
      <div className="jn-checkin-row">
        <span className="jn-checkin-label" style={{color}}>{label}</span>
        <div className="jn-checkin-bar">
          <div className="jn-checkin-fill" style={{width:`${pct}%`, background: color}}/>
        </div>
        <span className="jn-checkin-score" style={{color}}>{item.score}/7</span>
      </div>
      {item.note && <div className="jn-entry-text" style={{marginTop:8}}>{item.note}</div>}
    </div>
  );
}

function EvangelismCheckinCard({ item, t }) {
  const meta = TYPE_META.evangelism_checkin;
  return (
    <div className="jn-entry-inner">
      <div className="jn-entry-plan-meta">
        {(item.people || []).map((p, i) => (
          <span key={i} className="jn-chip" style={{ background: meta.bg, color: meta.color }}>
            {p.name}
          </span>
        ))}
      </div>
      {item.note && <div className="jn-entry-text" style={{ marginTop: 8 }}>{item.note}</div>}
    </div>
  );
}

function WellDiscussionCard({ item, t }) {
  const meta = TYPE_META.well_discussion;
  return (
    <div className="jn-entry-inner">
      <div className="jn-entry-plan-meta">
        {item.community?.name && (
          <span className="jn-chip" style={{background: meta.bg, color: meta.color}}>
            {item.community.name}
          </span>
        )}
        {(item.seriesTitle || item.weekLabel) && (
          <span className="jn-chip" style={{background:'var(--gray-1)',color:'var(--text-lt)'}}>
            {[item.seriesTitle, item.weekLabel].filter(Boolean).join(' · ')}
          </span>
        )}
        {item.threadTitle && (
          <span className="jn-chip" style={{background:'var(--gray-1)',color:'var(--text-lt)'}}>
            {item.threadTitle}
          </span>
        )}
      </div>
      <div className="jn-entry-section">
        <div className="jn-entry-section-label">
          {item.isQuestion ? t('wellAskedQuestion') : item.isReply ? t('wellReplied') : t('wellPosted')}
        </div>
        <div className="jn-entry-text" style={{whiteSpace:'pre-wrap'}}>{item.content}</div>
      </div>
    </div>
  );
}

function WellNoteCard({ item, t }) {
  const meta = TYPE_META.well_note;
  return (
    <div className="jn-entry-inner">
      <div className="jn-entry-plan-meta">
        {item.community?.name && (
          <span className="jn-chip" style={{background: meta.bg, color: meta.color}}>
            {item.community.name}
          </span>
        )}
        {(item.seriesTitle || item.weekLabel) && (
          <span className="jn-chip" style={{background:'var(--gray-1)',color:'var(--text-lt)'}}>
            {[item.seriesTitle, item.weekLabel].filter(Boolean).join(' · ')}
          </span>
        )}
        {item.threadTitle && (
          <span className="jn-chip" style={{background:'var(--gray-1)',color:'var(--text-lt)'}}>
            {item.threadTitle}
          </span>
        )}
      </div>
      <div className="jn-entry-section">
        <div className="jn-entry-section-label">
          <Lock size={11} style={{marginRight:4, verticalAlign:'middle'}}/>
          {t('wellMyNotes')}
        </div>
        <div className="jn-entry-text" style={{whiteSpace:'pre-wrap'}}>{item.content}</div>
      </div>
    </div>
  );
}

function JournalEntryCard({ item }) {
  const meta = TYPE_META.journal_entry;
  return (
    <div className="jn-entry-inner">
      {item.title && <div className="jn-adhoc-title">{item.title}</div>}
      <div className="jn-entry-text jn-adhoc-body">{item.body}</div>
      {item.tags?.length > 0 && (
        <div className="jn-entry-chips" style={{marginTop:8}}>
          {item.tags.map(tag => (
            <span key={tag} className="jn-chip" style={{background: meta.bg, color: meta.color}}>
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Build a single title-line string for each entry type ─────────────────────
function buildTitleLine(item, t) {
  switch (item.type) {
    case 'prayer_event':
      return t('prayedFor') + ' ' + (item.recipient?.name || '');
    case 'memorial_prayer':
      return t('remembering') + ' ' + (item.recipient?.name || '');
    case 'pray_now': {
      const moment = item.moment ? t('moments.' + item.moment, null, {ns:'prayNow'}) || item.moment : '';
      return t('types.pray_now') + (moment ? ' — ' + moment : '');
    }
    case 'bible_study':
      return t('types.bible_study') + (item.reference ? ' — ' + item.reference : '');
    case 'brp_reflection': {
      const unit = item.cadence === 'week' ? t('week') : t('day');
      return (item.planTitle || t('types.brp_reflection'))
        + (item.dayNumber != null ? ' — ' + unit + ' ' + item.dayNumber : '');
    }
    case 'growth_reflection': {
      const unit = item.cadence === 'week' ? t('week') : t('day');
      return (item.planTitle || t('types.growth_reflection'))
        + (item.unitNumber != null ? ' — ' + unit + ' ' + item.unitNumber : '');
    }
    case 'faith_checkin': {
      const labels = t('checkinLevels');
      const label = Array.isArray(labels) ? labels[(item.score || 1) - 1] : '';
      return t('types.faith_checkin') + (label ? ' — ' + label : '');
    }
    case 'evangelism_checkin':
      return t('types.evangelism_checkin') + (item.peopleCount ? ' — ' + t('sharedWith', item.peopleCount) : '');
    case 'well_discussion': {
      const verb = item.isQuestion ? t('wellAskedQuestion')
                : item.isReply     ? t('wellReplied')
                :                    t('wellPosted');
      const ctx = item.threadTitle || item.seriesTitle;
      return verb + (ctx ? ' — ' + ctx : '');
    }
    case 'well_note': {
      const ctx = item.threadTitle || item.seriesTitle;
      return t('types.well_note') + (ctx ? ' — ' + ctx : '');
    }
    case 'daily_rhythm_reflection': {
      const day = item.dayNumber != null ? `${t('day')} ${item.dayNumber}` : null;
      const head = item.theme || item.programTitle || t('types.daily_rhythm_reflection');
      return day ? `${head} — ${day}` : head;
    }
    case 'journal_entry':
      return item.title || item.body?.slice(0, 60) + (item.body?.length > 60 ? '…' : '');
    default:
      return item.type;
  }
}

function DailyRhythmReflectionCard({ item, t }) {
  const meta = TYPE_META[item.type];
  const prompts = item.prompts || {};
  const answers = item.answers || {};

  // The member's answers are PLAIN text (a real textarea), so they render as
  // text children — no dangerouslySetInnerHTML and no jn-entry-rich here. The
  // organizer's evening prompt was stripped of its tags server-side for the
  // same reason.
  const pairs = [
    { key: 'journal', q: prompts.journal, a: answers.journal },
    { key: 'evening', q: prompts.evening, a: answers.evening },
  ].filter(p => p.a && String(p.a).trim());

  return (
    <div className="jn-entry-inner">
      <div className="jn-entry-plan-meta">
        {item.programTitle && (
          <span className="jn-chip" style={{background: item.programColor ? item.programColor + '22' : meta.bg, color: item.programColor || meta.color}}>
            {item.programTitle}
          </span>
        )}
        {item.dayNumber != null && (
          <span className="jn-chip" style={{background: meta.bg, color: meta.color}}>
            {t('day')} {item.dayNumber}{item.theme ? ` · ${item.theme}` : ''}
          </span>
        )}
        {item.focus && (
          <span className="jn-chip" style={{background:'var(--gray-1)',color:'var(--text-lt)'}}>
            {item.focus}
          </span>
        )}
        {item.community && (
          <span className="jn-chip" style={{background:'var(--gray-1)',color:'var(--text-lt)'}}>
            {item.community.name}
          </span>
        )}
      </div>
      {item.verses && (
        <div className="jn-entry-passage" style={{color: meta.color}}>
          <BookOpen size={13}/> {item.verses}
        </div>
      )}
      {pairs.map(p => (
        <div key={p.key} className="jn-entry-qa">
          {p.q && <div className="jn-entry-q">{p.q}</div>}
          <div className="jn-entry-a">{p.a}</div>
        </div>
      ))}
    </div>
  );
}

// ── Entry Body Renderer (shared by detail modal) ─────────────────────────────

function renderEntryBody(item, t, lang) {
  switch (item.type) {
    case 'prayer_event':
    case 'memorial_prayer':
      return <PrayerEventCard item={item} t={t} lang={lang}/>;
    case 'pray_now':
      return <PrayNowCard item={item} t={t}/>;
    case 'bible_study':
      return <BibleStudyCard item={item} t={t}/>;
    case 'brp_reflection':
    case 'growth_reflection':
      return <ReflectionCard item={item} t={t}/>;
    case 'faith_checkin':
      return <FaithCheckinCard item={item} t={t}/>;
    case 'evangelism_checkin':
      return <EvangelismCheckinCard item={item} t={t}/>;
    case 'well_discussion':
      return <WellDiscussionCard item={item} t={t}/>;
    case 'well_note':
      return <WellNoteCard item={item} t={t}/>;
    case 'daily_rhythm_reflection':
      return <DailyRhythmReflectionCard item={item} t={t}/>;
    case 'journal_entry':
      return <JournalEntryCard item={item}/>;
    default:
      return null;
  }
}

// ── Entry Card Wrapper (single row — clicking opens detail modal) ────────────

function EntryCard({ item, t, lang, onOpen }) {
  const meta = TYPE_META[item.type] || TYPE_META.journal_entry;
  const titleLine = buildTitleLine(item, t);

  return (
    <div className="jn-card">
      <button className="jn-card-header" onClick={() => onOpen(item)}>
        <div className="jn-card-title-line">
          <span className="jn-hl" style={{'--hl': hexToRgbTriplet(meta.color)}}>
            <span className="jn-card-time">{fmtTime(item.date, lang)}</span>
            <span className="jn-card-sep">—</span>
            <span className="jn-card-preview-text">{titleLine}</span>
          </span>
          {item.isFav && <Star size={11} fill="#f59e0b" color="#f59e0b" style={{marginLeft:6, flexShrink:0}}/>}
        </div>
        <ChevronRight size={14} className="jn-card-toggle" aria-hidden="true"/>
      </button>
    </div>
  );
}

// ── Entry Detail Modal ───────────────────────────────────────────────────────

function EntryDetailModal({ item, onClose, onEdit, onDelete, onToggleFav, t, lang }) {
  // Close on Escape
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!item) return null;

  const meta = TYPE_META[item.type] || TYPE_META.journal_entry;
  const typeLabel = t(`types.${item.type}`);
  const isJournalEntry = item.type === 'journal_entry';

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal jn-detail-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="jn-detail-header">
          <span className="jn-detail-icon" style={{ background: meta.color }}>
            {meta.icon}
          </span>
          <div className="jn-detail-title">
            <h3>{typeLabel}</h3>
            <span className="jn-detail-date">
              {fmtDate(item.date, lang)} · {fmtTime(item.date, lang)}
            </span>
          </div>
          <button className="jn-detail-close" onClick={onClose} aria-label={t('close')}>
            <X size={20}/>
          </button>
        </header>

        <div className="jn-detail-body">
          {renderEntryBody(item, t, lang)}
        </div>

        {isJournalEntry && (
          <footer className="jn-detail-actions">
            <button className="btn btn-outline" onClick={() => onToggleFav(item)}>
              {item.isFav
                ? <><Star size={14} fill="#f59e0b" color="#f59e0b"/> {t('unstar')}</>
                : <><StarOff size={14}/> {t('starred')}</>}
            </button>
            <button className="btn btn-outline" onClick={() => onEdit(item)}>
              <PenLine size={14}/> {t('editEntry')}
            </button>
            <button className="btn jn-detail-danger" onClick={() => onDelete(item)}>
              <Trash2 size={14}/> {t('deleteEntry')}
            </button>
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── Stats Sidebar ─────────────────────────────────────────────────────────────

function StatsSidebar({ items, t }) {
  const prayers   = items.filter(i => i.type === 'prayer_event' || i.type === 'memorial_prayer').length;
  const prayNows  = items.filter(i => i.type === 'pray_now').length;
  const entries   = items.filter(i => i.type === 'journal_entry').length;
  const words     = items
    .filter(i => i.type === 'journal_entry')
    .reduce((sum, i) => sum + wordCount(i.body), 0);

  return (
    <div className="jn-sidebar-stats">
      <div className="jn-stat-row">
        <HeartHandshake size={14} color="#0d9488"/>
        <span>{t('totalPrayers', prayers)}</span>
      </div>
      <div className="jn-stat-row">
        <Sparkles size={14} color="#f59e0b"/>
        <span>{prayNows} Pray Now</span>
      </div>
      <div className="jn-stat-row">
        <PenLine size={14} color="#6366f1"/>
        <span>{t('totalEntries', entries)}</span>
      </div>
      {words > 0 && (
        <div className="jn-stat-row">
          <BookOpen size={14} color="#2563eb"/>
          <span>{t('totalWords', words)}</span>
        </div>
      )}
    </div>
  );
}

// ── Week Navigator ────────────────────────────────────────────────────────────

function WeekNav({ weekStart, onChange, activity, items, t, lang, onDayClick }) {
  // Active days = days inside the current week that have at least one item.
  const dayKeys = Array.from({ length: 7 }, (_, i) => fmtLocalDateKey(addDays(weekStart, i)));
  const activeDays = dayKeys.filter(k => (activity?.[k] || 0) > 0).length;

  function jumpToDate(value) {
    if (!value) return;
    // Parse YYYY-MM-DD as local midnight to avoid TZ shift.
    const [y, m, d] = value.split('-').map(Number);
    const target = new Date(y, m - 1, d);
    onChange(startOfWeekSunday(target));
  }

  const today = new Date();
  const todayKey = fmtLocalDateKey(today);

  return (
    <div className="jn-weeknav">
      <div className="jn-weeknav-row">
        <button className="jn-weeknav-btn" aria-label={t('prevWeek')}
                onClick={() => onChange(addDays(weekStart, -7))}>
          <ChevronLeft size={16}/>
        </button>

        <div className="jn-weeknav-label">
          <span className="jn-weeknav-range">
            {t('weekTitle', {
              week: isoWeekNumber(addDays(weekStart, 3)),
              range: fmtWeekRange(weekStart, lang),
            })}
          </span>
          <span className="jn-weeknav-meta">
            {t('weekStats', { entries: items.length, days: activeDays })}
          </span>
        </div>

        <button className="jn-weeknav-btn" aria-label={t('nextWeek')}
                disabled={isThisWeek(weekStart)}
                onClick={() => onChange(addDays(weekStart, 7))}>
          <ChevronRight size={16}/>
        </button>
      </div>

      <div className="jn-weeknav-row jn-weeknav-row-actions">
        <input type="date" className="jn-weeknav-date"
               max={fmtLocalDateKey(today)}
               value={fmtLocalDateKey(weekStart)}
               onChange={e => jumpToDate(e.target.value)}
               aria-label={t('jumpToDate')}/>
        <button className="jn-weeknav-today"
                disabled={isThisWeek(weekStart)}
                onClick={() => onChange(startOfWeekSunday(new Date()))}>
          {t('today')}
        </button>
      </div>

      {/* Day strip — one cell per day with a dot when content exists.
          Clicking a cell scrolls to that day's section below. */}
      <div className="jn-weeknav-days">
        {dayKeys.map((key, i) => {
          const date  = addDays(weekStart, i);
          const count = activity?.[key] || 0;
          const isToday = key === todayKey;
          return (
            <button
              key={key}
              type="button"
              className={`jn-weeknav-day ${count > 0 ? 'has-content' : ''} ${isToday ? 'today' : ''}`}
              onClick={() => onDayClick && onDayClick(key)}
              aria-label={date.toLocaleDateString(lang || undefined, { weekday:'long', month:'long', day:'numeric' })}>
              <span className="jn-weeknav-day-name">
                {date.toLocaleDateString(lang || undefined, { weekday:'narrow' })}
              </span>
              <span className="jn-weeknav-day-num">{date.getDate()}</span>
              <span className={`jn-weeknav-day-dot ${count > 0 ? 'on' : ''}`}/>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Screen ───────────────────────────────────────────────────────────────

export default function JournalScreen() {
  // Rendered by HomeLayout/GrowLayout and placed here so the strip sits under
  // this screen's title, matching Prayer and People. Null when this screen is
  // reached outside its section layout.
  const { sectionTabs } = useOutletContext() || {};
  const { t, lang } = useTranslation('journal');
  const [weekStart,   setWeekStart]   = useState(() => startOfWeekSunday(new Date()));
  const [items,       setItems]       = useState([]);
  const [activity,    setActivity]    = useState({});
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState('all');
  const [search,      setSearch]      = useState('');
  const [favOnly,     setFavOnly]     = useState(false);
  const [favEntries,  setFavEntries]  = useState([]);    // all-time starred entries
  const [showCompose, setShowCompose] = useState(false);
  const [editEntry,   setEditEntry]   = useState(null);
  const [detailItem,  setDetailItem]  = useState(null);
  const searchRef = useRef(null);

  const activeFilter = UNIQUE_FILTERS.find(f => f.key === filter) || UNIQUE_FILTERS[0];

  // ── Week-bounded fetch ──────────────────────────────────────────────────────
  // The activity window covers ±6 weeks around the visible week so the day-dot
  // strip and month picker can show neighbouring activity without a refetch
  // each time the user steps a single week.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = weekStart.toISOString();
      const to   = addDays(weekStart, 7).toISOString();
      const activityFrom = addDays(weekStart, -42).toISOString();
      const activityTo   = addDays(weekStart, 49).toISOString();

      const [timelineRes, activityRes] = await Promise.all([
        journalApi.timeline({
          from, to,
          ...(activeFilter.types ? { types: activeFilter.types } : {}),
        }),
        journalApi.activity({ from: activityFrom, to: activityTo }),
      ]);

      setItems(timelineRes.items || []);
      setActivity(activityRes.counts || {});
    } catch (err) {
      console.error('Journal load error:', err);
    } finally { setLoading(false); }
  }, [weekStart, filter]);

  useEffect(() => { load(); }, [load]);

  // ── Fav-only mode loads all-time entries (small, bounded by user behavior) ──
  useEffect(() => {
    if (!favOnly) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await journalApi.listEntries();
        if (cancelled) return;
        const favs = (data.entries || [])
          .filter(e => e.isFav)
          .map(e => ({
            ...e,
            type: 'journal_entry',
            date: e.entryDate || e.createdAt,
          }));
        setFavEntries(favs);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [favOnly]);

  function handleEntrySaved(entry, isEdit) {
    if (isEdit) {
      setItems(prev => prev.map(i => i.id === entry.id && i.type === 'journal_entry'
        ? { ...i, ...entry, type: 'journal_entry', date: entry.entryDate || entry.date || i.date }
        : i));
    } else {
      const newItem = { ...entry, type: 'journal_entry', date: entry.entryDate || entry.createdAt };
      setItems(prev => [newItem, ...prev]);
    }
  }

  async function handleDelete(item) {
    if (!window.confirm(t('deleteEntry'))) return;
    try {
      await journalApi.deleteEntry(item.id);
      setItems(prev => prev.filter(i => !(i.id === item.id && i.type === 'journal_entry')));
      setDetailItem(null);
    } catch {}
  }

  async function handleToggleFav(item) {
    try {
      const updated = await journalApi.updateEntry(item.id, { isFav: !item.isFav });
      const newFav = updated.entry?.isFav ?? !item.isFav;
      setItems(prev => prev.map(i => i.id === item.id && i.type === 'journal_entry'
        ? { ...i, isFav: newFav } : i));
      setDetailItem(prev => prev && prev.id === item.id && prev.type === 'journal_entry'
        ? { ...prev, isFav: newFav } : prev);
    } catch {}
  }

  function handleEditFromDetail(item) {
    setDetailItem(null);
    setEditEntry(item);
  }

  // ── Filter + search ─────────────────────────────────────────────────────────
  // When favOnly is on, switch the source from the current week to the
  // all-time list of starred journal entries so users can scan their
  // favourites across history without manually paging through weeks.
  const baseItems = favOnly ? favEntries : items;
  const filtered = baseItems.filter(item => {
    if (favOnly && item.type !== 'journal_entry') return false;
    if (favOnly && !item.isFav) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const searchable = [
      item.recipient?.name,
      item.prayer, item.notes, item.body, item.title,
      item.reference, item.planTitle, item.verses,
      item.programTitle, item.theme, item.focus,
      ...(Object.values(item.answers || {})),
      item.note,
      item.content, item.threadTitle, item.seriesTitle, item.community?.name,
    ].filter(Boolean).join(' ').toLowerCase();
    return searchable.includes(q);
  });

  const groups = groupByDate(filtered);

  // When viewing a week (not fav-only), guarantee a section for every day so
  // the strip dots line up 1:1 with day headings below — even empty days.
  let sections;
  if (favOnly) {
    sections = groups.map(([key, items]) => ({ key, items, empty: false }));
  } else {
    const byKey = Object.fromEntries(groups);
    sections = Array.from({ length: 7 }, (_, i) => {
      const key = fmtLocalDateKey(addDays(weekStart, i));
      const items = byKey[key] || [];
      return { key, items, empty: items.length === 0 };
    });
  }

  function scrollToDay(dateKey) {
    const el = document.getElementById(`jn-day-${dateKey}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="page jn-page">
      {/* ── Header ── */}
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PageGuide screenKey="journal" />
            <button className="btn btn-primary jn-compose-btn" onClick={() => setShowCompose(true)}>
              <Plus size={14}/> {t('newEntry')}
            </button>
          </div>
        }
      />
      {sectionTabs}

      {/* ── Controls (week strip, search, filters) — OFF the notepad ── */}
      <div className="jn-layout">
        <aside className="jn-sidebar">
          {/* Search */}
          <div className="jn-search-wrap">
            <Search size={14} className="jn-search-icon"/>
            <input ref={searchRef} className="jn-search" value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('searchPlaceholder')}/>
            {search && <button className="jn-search-clear" onClick={() => setSearch('')}><X size={13}/></button>}
          </div>

          {/* Filters */}
          <div className="jn-filter-section">
            <div className="jn-filter-label"><Filter size={12}/> Filters</div>
            {UNIQUE_FILTERS.map(f => (
              <button key={f.key}
                className={`jn-filter-btn ${filter === f.key ? 'active' : ''}`}
                onClick={() => setFilter(f.key)}>
                {t(f.labelKey)}
              </button>
            ))}
            <button
              className={`jn-filter-btn ${favOnly ? 'active' : ''}`}
              onClick={() => setFavOnly(v => !v)}>
              <Star size={12} style={{marginRight:4}}/>{t('favOnly')}
            </button>
          </div>

          {/* Stats — reflects the visible window (current week, or all-time when favOnly) */}
          {!loading && filtered.length > 0 && (
            <div className="jn-filter-section" style={{marginTop:8}}>
              <div className="jn-filter-label">{favOnly ? t('allTime') : t('thisWeek')}</div>
              <StatsSidebar items={filtered} t={t}/>
            </div>
          )}
        </aside>

        <main className="jn-main">
          {/* Header chrome lives OUTSIDE the notepad */}
          <div className="jn-header">
            {!favOnly && (
              <WeekNav
                weekStart={weekStart}
                onChange={setWeekStart}
                activity={activity}
                items={items}
                t={t}
                lang={lang}
                onDayClick={scrollToDay}/>
            )}

            {/* Mobile search + filter strip */}
            <div className="jn-mobile-controls">
              <div className="jn-search-wrap">
                <Search size={14} className="jn-search-icon"/>
                <input className="jn-search" value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={t('searchPlaceholder')}/>
                {search && <button className="jn-search-clear" onClick={() => setSearch('')}><X size={13}/></button>}
              </div>
              <div className="jn-filter-strip">
                {UNIQUE_FILTERS.map(f => (
                  <button key={f.key}
                    className={`jn-filter-chip ${filter === f.key ? 'active' : ''}`}
                    onClick={() => setFilter(f.key)}>
                    {t(f.labelKey)}
                  </button>
                ))}
                <button
                  className={`jn-filter-chip ${favOnly ? 'active' : ''}`}
                  onClick={() => setFavOnly(v => !v)}>
                  <Star size={11} style={{marginRight:3}}/>{t('favOnly')}
                </button>
              </div>
            </div>

            {search && !favOnly && (
              <div className="jn-search-scope">
                {t('searchScopedToWeek')}
              </div>
            )}
          </div>

          {/* Notepad — only journal entries live here */}
          <div className="jn-notebook">
          <div className="jn-paper">
            {loading ? (
              <div className="jn-loading">
                <span className="spinner" style={{width:32,height:32}}/>
              </div>
            ) : filtered.length === 0 && favOnly ? (
              <div className="empty-state jn-empty">
                <BookOpen size={44} strokeWidth={1.25} color="var(--text-lt)"/>
                <h3>{search ? t('noResults') : t('emptyFavorites')}</h3>
              </div>
            ) : filtered.length === 0 ? (
              <div className="empty-state jn-empty">
                <BookOpen size={44} strokeWidth={1.25} color="var(--text-lt)"/>
                <h3>{search ? t('noResults') : t('emptyWeek')}</h3>
                {!search && <p>{t('emptyWeekDesc')}</p>}
                {!search && (
                  <button className="btn btn-primary" style={{background:'var(--jn-accent)',borderColor:'var(--jn-accent)'}} onClick={() => setShowCompose(true)}>
                    <Plus size={14}/> {t('newEntry')}
                  </button>
                )}
              </div>
            ) : (
              <>
                {sections.map(({ key: dateKey, items: dayItems, empty }) => (
                  <div key={dateKey} className={`jn-day-group ${empty ? 'is-empty' : ''}`}>
                    <div className="jn-day-divider" id={`jn-day-${dateKey}`}>
                      <span className="jn-day-label">{fmtDate(dateKey + 'T12:00:00', lang)}</span>
                      {!empty && <span className="jn-day-count">{t('totalEntries', dayItems.length)}</span>}
                    </div>
                    {!empty && (
                      <div className="jn-day-entries">
                        {dayItems.map(item => (
                          <EntryCard key={item.type + item.id} item={item} t={t} lang={lang}
                            onOpen={setDetailItem}/>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>{/* /jn-paper */}
          </div>{/* /jn-notebook */}
        </main>
      </div>

      {/* Mobile FAB */}
      <button className="jn-fab" onClick={() => setShowCompose(true)} aria-label={t('newEntry')}>
        <PenLine size={20}/>
      </button>

      {/* Detail modal — opens when a journal row is clicked */}
      <EntryDetailModal
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onEdit={handleEditFromDetail}
        onDelete={handleDelete}
        onToggleFav={handleToggleFav}
        t={t}
        lang={lang}/>

      {/* Compose / edit modal */}
      {(showCompose || editEntry) && (
        <EntryModal
          entry={editEntry}
          onClose={() => { setShowCompose(false); setEditEntry(null); }}
          onSaved={handleEntrySaved}/>
      )}
    </div>
  );
}
